import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Segmented,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  TimePicker,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  CloseOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  ExecutionBoqLine,
  ExecutionNormCell,
  ExecutionSlotDef,
  ExecutionSlotKey,
  MeasuredScope,
  activityExecutionApis,
  driveChainApis,
  executionNormApis,
  uploadActualPhotosWeb,
} from "../../../api/ActivityExecution";
import { logHoursApis } from "../../../api/TimeLog";
import {
  ExecutionStretch,
  computeEntryPreview,
  dayOverlapConflicts,
  fmtNorm,
  fmtQty,
  incompleteStretchCount,
  isCompleteStretch,
  round4,
  stretchLength,
  sumStretches,
  supportsStretches,
  toDateOnly,
} from "./executionNorm";
import { useWbsRecordName } from "../shared/useWbsCodes";
import "./execution.css";
import CapturedChain from "./CapturedChain";

// One day's execution at one face, captured in a drawer so the register behind
// it stays the page. Three sections, in the order the work happens:
//
//   Where and when  →  Executed at the face  →  What that produced
//
// Rock-class slots take NO typed quantity: advance is recorded as chainage
// STRETCHES (From CH → To CH), exactly as on the mobile app, and the quantity
// derives as Σ|to−from|. The cum method/zone slots keep a plain input. The
// derived BOQ lines carry an ACTUAL tier — pending until site records the
// actual work done, stretch-wise, with optional evidence photos.

const TIME_FORMAT = "hh:mm A";
const MAX_PHOTOS = 3;
const DEVIATION_REMARK_PERCENT = 10;

interface MeasuredEntry {
  /** Line total — sum of stretch actuals, or the scalar on no-stretch days. */
  value: string;
  remarks: string;
  stretchActuals?: Array<{ fromChainage: string; toChainage: string; value: string }>;
  /** Local files, uploaded after the day saves. */
  photos?: File[];
}

interface Props {
  open: boolean;
  projectId: number;
  // Supplied when editing an existing entry; the drawer loads it and locks
  // the identity fields, because changing them would silently target a
  // different record rather than edit this one.
  editing?: { activityId: number; executionDate: string; shiftId?: number } | null;
  onClose: () => void;
  onSaved: () => void;
  onConfigureNorms: (activityId?: number) => void;
  /** Date a new entry starts on (the calendar's picked day); today when absent. */
  initialDate?: string;
}

const DailyExecutionDrawer: React.FC<Props> = ({
  open,
  projectId,
  editing,
  onClose,
  onSaved,
  onConfigureNorms,
  initialDate,
}) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [shiftOptions, setShiftOptions] = useState<any[]>([]);

  const [phaseId, setPhaseId] = useState<number | undefined>();
  const [activityId, setActivityId] = useState<number | undefined>();
  const [executionDate, setExecutionDate] = useState<Dayjs>(dayjs());
  const [shiftId, setShiftId] = useState<number | undefined>();
  const [workWindow, setWorkWindow] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [remarks, setRemarks] = useState("");
  const [quantities, setQuantities] = useState<Partial<Record<ExecutionSlotKey, number | null>>>({});
  // Chainage stretches per slot — any rows present means the slot's quantity
  // IS their summed length, same rule as the server.
  const [stretches, setStretches] = useState<Partial<Record<ExecutionSlotKey, ExecutionStretch[]>>>({});
  // Actual work done per BOQ line (taskId-keyed) — the measurement tier.
  const [measured, setMeasured] = useState<Record<number, MeasuredEntry>>({});
  const [actualTarget, setActualTarget] = useState<any>(null);

  const [slots, setSlots] = useState<ExecutionSlotDef[]>([]);
  const [norms, setNorms] = useState<ExecutionNormCell[]>([]);
  const [tasks, setTasks] = useState<ExecutionBoqLine[]>([]);
  const [faceChain, setFaceChain] = useState<any>(null);

  const [loadingNorms, setLoadingNorms] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingEntryId, setExistingEntryId] = useState<number | null>(null);
  const [error, setError] = useState("");
  // Which norm version governs THIS date. The matrix is effective-dated, so a
  // back-dated DPR must be previewed — and saved — against the matrix that was
  // in force then, not the newest one. Resolved server-side by the entry lookup.
  // Per-BOQ-line progress for the SAVED entry: cumulative through this entry's
  // date and that as a % of plan. Server-side only — an unsaved draft has not
  // been counted yet, so there is nothing honest to show against it.
  const [savedLines, setSavedLines] = useState<Record<number, any>>({});
  const [dateVersion, setDateVersion] = useState<{
    versionId: number;
    effectiveFrom: string;
    normsInForce: boolean;
    isStale: boolean;
  } | null>(null);
  // Task Monitoring, as of THIS entry's date. When the project tracks lines,
  // the derived table opens filtered to the tracked ones — those are what site
  // is expected to measure — and the save says so (`measuredScope`). Hidden
  // lines are still derived and still counted; the filter is a view.
  const [monitoring, setMonitoring] = useState<{ configured: boolean; trackedCount: number }>({
    configured: false,
    trackedCount: 0,
  });
  const [trackedOnly, setTrackedOnly] = useState(false);

  const isEdit = !!editing;

  // Reset to a clean slate every time the drawer opens, so yesterday's entry
  // never bleeds into today's.
  useEffect(() => {
    if (!open) return;
    setError("");
    setExistingEntryId(null);
    setQuantities({});
    setStretches({});
    setMeasured({});
    setRemarks("");
    if (editing) {
      setActivityId(editing.activityId);
      setPhaseId(undefined);
      setExecutionDate(dayjs(editing.executionDate));
      setShiftId(editing.shiftId);
    } else {
      setPhaseId(undefined);
      setActivityId(undefined);
      setExecutionDate(initialDate ? dayjs(initialDate) : dayjs());
      setShiftId(undefined);
      setWorkWindow(null);
    }
  }, [open, editing]);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    Promise.all([
      executionNormApis.listActivities(projectId),
      logHoursApis.getShifts(projectId),
    ])
      .then(([activityRes, shiftRes]) => {
        if (cancelled) return;
        setActivities(activityRes?.data?.activities || []);
        const list = shiftRes?.data?.response || shiftRes?.data || [];
        setShiftOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => !cancelled && setError("Could not load activities"));
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  // The drive's face position — the chainage ledger's one-line answer. Fails
  // soft: no chip is just no chip.
  useEffect(() => {
    if (!open || !activityId) {
      setFaceChain(null);
      return;
    }
    let cancelled = false;
    driveChainApis
      .get(activityId)
      .then((res) => {
        if (!cancelled && res?.status === true) setFaceChain(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, activityId, existingEntryId]);

  // Norms for the chosen activity AS OF THE CHOSEN DATE — these are what make
  // the preview possible, and what the save will actually use.
  //
  // Deliberately waits for `dateVersion`: fetching the newest matrix first would
  // flash the wrong numbers on a back-dated entry, and on an activity whose
  // slots were relabelled it would offer inputs the day cannot be captured
  // against.
  useEffect(() => {
    if (!open || !activityId || !dateVersion) {
      setSlots([]);
      setNorms([]);
      setTasks([]);
      setMonitoring({ configured: false, trackedCount: 0 });
      setTrackedOnly(false);
      return;
    }
    if (!dateVersion.normsInForce) {
      setSlots([]);
      setNorms([]);
      setTasks([]);
      setMonitoring({ configured: false, trackedCount: 0 });
      setTrackedOnly(false);
      return;
    }
    let cancelled = false;
    setLoadingNorms(true);
    executionNormApis
      .getByActivity(activityId, dateVersion.versionId, toDateOnly(executionDate.toDate()))
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          setError(res?.message || "Could not load execution norms");
          return;
        }
        setError("");
        setSlots(res.data?.slots || []);
        setNorms(res.data?.norms || []);
        setTasks(res.data?.tasks || []);
        // gRPC omits false/0 — read both with defaults.
        const configured = res.data?.monitoringConfigured === true;
        setMonitoring({
          configured,
          trackedCount: Number(res.data?.monitoredTaskCount) || 0,
        });
        setTrackedOnly(configured);
      })
      .catch(() => !cancelled && setError("Could not load execution norms"))
      .finally(() => !cancelled && setLoadingNorms(false));
    return () => {
      cancelled = true;
    };
    // executionDate is folded into dateVersion (the entry lookup resets it on
    // every date change), so it is not a separate dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activityId, dateVersion]);

  // Look for an entry already captured for this activity + date + shift. This
  // is what turns a repeat visit into an edit instead of a silent duplicate.
  const loadEntry = useCallback(() => {
    if (!open || !activityId || !executionDate) return;
    // Unknown until the server answers. Holding null (rather than a stale value)
    // is what keeps the norms effect from previewing the wrong matrix.
    setDateVersion(null);
    let cancelled = false;
    setLoadingEntry(true);
    activityExecutionApis
      .getEntry({
        activityId,
        executionDate: toDateOnly(executionDate.toDate()),
        ...(shiftId ? { shiftId } : {}),
      })
      .then((res) => {
        if (cancelled || res?.status !== true) return;
        const data = res.data || {};
        setDateVersion({
          versionId: Number(data.normVersionId) || 0,
          effectiveFrom: data.normEffectiveFrom || "",
          normsInForce: data.normsInForce !== false,
          isStale: data.isStale === true,
        });
        if (data.exists) {
          setExistingEntryId(data.entryId);
          const byTask: Record<number, any> = {};
          const measuredNext: Record<number, MeasuredEntry> = {};
          (data.lines || []).forEach((line: any) => {
            byTask[Number(line.taskId)] = line;
            if (line.measuredValue) {
              measuredNext[Number(line.taskId)] = {
                value: line.measuredValue,
                remarks: line.measuredRemarks || "",
                ...(line.measuredStretches?.length
                  ? {
                      stretchActuals: line.measuredStretches.map((actual: any) => ({
                        fromChainage: actual.fromChainage,
                        toChainage: actual.toChainage,
                        value: actual.measuredValue,
                      })),
                    }
                  : {}),
              };
            }
          });
          setSavedLines(byTask);
          setMeasured(measuredNext);
          setRemarks(data.remarks || "");
          setWorkWindow(
            data.startTime && data.endTime
              ? [dayjs(data.startTime, "HH:mm"), dayjs(data.endTime, "HH:mm")]
              : null
          );
          const next: Partial<Record<ExecutionSlotKey, number | null>> = {};
          const stretchesNext: Partial<Record<ExecutionSlotKey, ExecutionStretch[]>> = {};
          (data.slots || []).forEach((slot: any) => {
            const qty = Number(slot.actualExecutionQty);
            next[slot.slotKey as ExecutionSlotKey] =
              Number.isFinite(qty) && qty !== 0 ? qty : null;
            if (slot.stretches?.length) {
              stretchesNext[slot.slotKey as ExecutionSlotKey] = slot.stretches.map(
                (stretch: any) => ({
                  fromChainage: stretch.fromChainage,
                  toChainage: stretch.toChainage,
                })
              );
            }
          });
          setQuantities(next);
          setStretches(stretchesNext);
        } else {
          setExistingEntryId(null);
          setQuantities({});
          setStretches({});
          setMeasured({});
          setRemarks("");
          setSavedLines({});
        }
      })
      .finally(() => !cancelled && setLoadingEntry(false));
    return () => {
      cancelled = true;
    };
  }, [open, activityId, executionDate, shiftId]);

  useEffect(() => {
    const cleanup = loadEntry();
    return cleanup;
  }, [loadEntry]);

  const activityLabel = useWbsRecordName(activities, "activityId", "activityName");

  // Phase first, then face: a project has ~50 activities and scrolling them all
  // to find one face is the slow part of capturing a day.
  const phaseOptions = useMemo(() => {
    const seen = new Map<number, string>();
    activities.forEach((a: any) => {
      if (a.phaseId && !seen.has(a.phaseId)) {
        seen.set(a.phaseId, a.phaseName || `Phase ${a.phaseId}`);
      }
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [activities]);

  const activityOptions = useMemo(
    () =>
      activities
        .filter((a: any) => !phaseId || a.phaseId === phaseId)
        .map((a: any) => ({
          value: a.activityId,
          label: activityLabel(a),
          disabled: !a.isConfigured,
        })),
    [activities, phaseId, activityLabel]
  );
  const activeSlots = useMemo(() => slots.filter((s) => s.isActive !== false), [slots]);

  // The unit the Executed Qty column is keyed in — read from the slot
  // definitions; blank when the slots disagree.
  const driverUom = useMemo(() => {
    const units = Array.from(
      new Set(activeSlots.map((s) => (s.uomName || "").trim()).filter(Boolean))
    );
    if (units.length === 0) return "m";
    return units.length === 1 ? units[0] : "";
  }, [activeSlots]);
  const executedQtyTitle = driverUom ? `Executed Qty (${driverUom})` : "Executed Qty";
  const selectedActivity = useMemo(
    () => activities.find((a) => a.activityId === activityId),
    [activities, activityId]
  );

  const minutesBetween = (from: Dayjs, to: Dayjs) => {
    const a = from.hour() * 60 + from.minute();
    const b = to.hour() * 60 + to.minute();
    return b > a ? b - a : b + 1440 - a; // end at or before start = crossed midnight
  };

  // The shift window is a starting point, not a limit — so prefill only fills
  // an EMPTY window and never overwrites hours someone has typed.
  const handleShiftChange = (value: number | undefined) => {
    setShiftId(value);
    if (!value || (workWindow?.[0] && workWindow?.[1])) return;
    const shift = shiftOptions.find((o) => o.shiftId === value);
    if (shift?.shiftStartTime && shift?.shiftEndTime) {
      setWorkWindow([
        dayjs(String(shift.shiftStartTime), "HH:mm:ss"),
        dayjs(String(shift.shiftEndTime), "HH:mm:ss"),
      ]);
    }
  };

  const windowHours = useMemo(() => {
    if (!workWindow?.[0] || !workWindow?.[1]) return "";
    const minutes = minutesBetween(workWindow[0], workWindow[1]);
    const label = `${Math.floor(minutes / 60)}h ${`${minutes % 60}`.padStart(2, "0")}m`;
    const crosses =
      workWindow[1].hour() * 60 + workWindow[1].minute() <=
      workWindow[0].hour() * 60 + workWindow[0].minute();
    const shift = shiftOptions.find((o) => o.shiftId === shiftId);
    let variance = "";
    if (shift?.shiftStartTime && shift?.shiftEndTime) {
      const nominal = minutesBetween(
        dayjs(String(shift.shiftStartTime), "HH:mm:ss"),
        dayjs(String(shift.shiftEndTime), "HH:mm:ss")
      );
      const delta = minutes - nominal;
      if (delta !== 0) {
        const abs = Math.abs(delta);
        variance = ` · ${delta > 0 ? "+" : "−"}${Math.floor(abs / 60)}h ${`${abs % 60}`.padStart(2, "0")}m vs shift`;
      }
    }
    return `${label}${crosses ? " · crosses midnight" : ""}${variance}`;
  }, [workWindow, shiftId, shiftOptions]);

  // ---- effective quantities: stretches win over the typed figure ---------
  // Same predicate as the mobile app and the server: ANY stretch rows on a
  // slot = stretch mode; the quantity is the sum of the COMPLETE rows.
  const effectiveQuantities = useMemo(() => {
    const next: Partial<Record<ExecutionSlotKey, number>> = {};
    for (const slot of activeSlots) {
      const rows = stretches[slot.slotKey] || [];
      if (rows.length) {
        next[slot.slotKey] = sumStretches(rows.filter(isCompleteStretch));
      } else if (quantities[slot.slotKey] != null) {
        next[slot.slotKey] = Number(quantities[slot.slotKey]);
      }
    }
    return next;
  }, [activeSlots, stretches, quantities]);

  // Day-level geometry validation — ACROSS classes, mirroring the server
  // guard: one metre of tunnel is one rock class. Overlaps BLOCK the save.
  const dayConflicts = useMemo(() => dayOverlapConflicts(stretches), [stretches]);
  const incompleteCount = useMemo(() => incompleteStretchCount(stretches), [stretches]);


  const slotLabelOf = useCallback(
    (key: string) => slots.find((slot) => slot.slotKey === key)?.slotLabel || key,
    [slots]
  );

  // The stretches on the form right now, for the chain to draw in its own lane
  // (green when clear, red where they reach into captured ground).
  const typedStretches = useMemo(
    () =>
      Object.entries(stretches).flatMap(([slotKey, rows]: [string, any]) =>
        (rows || []).filter(isCompleteStretch).map((stretch: any) => ({
          from: Number(stretch.fromChainage),
          to: Number(stretch.toChainage),
          label: slotLabelOf(slotKey),
        }))
      ),
    [stretches, slotLabelOf]
  );

  const preview = useMemo(
    () =>
      computeEntryPreview({
        slotQuantities: effectiveQuantities,
        slotDefs: slots,
        norms,
        tasks,
      }),
    [effectiveQuantities, slots, norms, tasks]
  );

  // Is this line tracked on this date? The saved entry's lines know (they
  // carry the flag as of the day they were saved); before a save, the norms
  // tasks — fetched as of the entry date — are the source.
  const isTrackedLine = useCallback(
    (taskId: number) => {
      const saved = savedLines[taskId];
      // gRPC omits `false`, so an absent flag on a saved line IS "untracked".
      if (saved) return saved.isMonitored === true;
      return tasks.find((task) => task.taskId === taskId)?.isMonitored === true;
    },
    [savedLines, tasks]
  );
  const trackedFilterOn = monitoring.configured && trackedOnly;
  const trackedPreviewCount = useMemo(
    () => (monitoring.configured ? preview.filter((line) => isTrackedLine(line.taskId)).length : 0),
    [monitoring.configured, preview, isTrackedLine]
  );
  // What the table SHOWS. Every count below still describes `preview` — all of
  // what was derived — never just the visible rows.
  const visiblePreview = useMemo(
    () => (trackedFilterOn ? preview.filter((line) => isTrackedLine(line.taskId)) : preview),
    [trackedFilterOn, preview, isTrackedLine]
  );
  const hiddenCount = preview.length - visiblePreview.length;

  const hasQuantity = useMemo(
    () => Object.values(effectiveQuantities).some((v) => v != null && Number(v) !== 0),
    [effectiveQuantities]
  );
  // Two different absences, and conflating them would send the user to the wrong
  // fix: NO MATRIX COVERS THIS DATE (the activity is configured, just not back
  // this far) vs NEVER CONFIGURED AT ALL.
  const noVersionForDate =
    !!activityId && !!dateVersion && dateVersion.normsInForce === false;
  const notConfigured =
    !!activityId && !noVersionForDate && !loadingNorms && !!dateVersion && norms.length === 0;
  const isFuture = executionDate?.isAfter(dayjs(), "day");

  // ---- stretch row mutations ---------------------------------------------
  const patchStretches = (slotKey: ExecutionSlotKey, rows: ExecutionStretch[]) =>
    setStretches((current) => ({ ...current, [slotKey]: rows }));
  const addStretch = (slotKey: ExecutionSlotKey) => {
    const rows = stretches[slotKey] || [];
    const last = [...rows].reverse().find(isCompleteStretch);
    // The face continues where it left off — prefill from = the last 'to'.
    patchStretches(slotKey, [
      ...rows,
      { fromChainage: last?.toChainage ?? "", toChainage: "" },
    ]);
  };

  const handleSave = async () => {
    if (!activityId) return;
    setSaving(true);
    try {
      const measuredPayload = Object.entries(measured)
        .filter(([, m]) => m.value !== "")
        .map(([taskId, m]) => ({
          taskId: Number(taskId),
          measuredValue: m.value,
          ...(m.remarks ? { remarks: m.remarks } : {}),
          ...(m.stretchActuals?.length
            ? {
                stretchActuals: m.stretchActuals
                  .filter((actual) => actual.value !== "")
                  .map((actual) => ({
                    fromChainage: actual.fromChainage,
                    toChainage: actual.toChainage,
                    measuredValue: actual.value,
                  })),
              }
            : {}),
        }));
      const res = await activityExecutionApis.save({
        activityId,
        executionDate: toDateOnly(executionDate.toDate()),
        ...(shiftId ? { shiftId } : {}),
        ...(workWindow?.[0] && workWindow?.[1]
          ? {
              startTime: workWindow[0].format("HH:mm"),
              endTime: workWindow[1].format("HH:mm"),
            }
          : {}),
        slots: activeSlots
          .map((s) => {
            const rows = stretches[s.slotKey] || [];
            const complete = rows.filter(isCompleteStretch);
            if (rows.length) {
              return {
                slotKey: s.slotKey,
                actualExecutionQty: String(sumStretches(complete)),
                stretches: complete.map((stretch) => ({
                  fromChainage: stretch.fromChainage,
                  toChainage: stretch.toChainage,
                })),
              };
            }
            if (quantities[s.slotKey] == null) return null;
            return {
              slotKey: s.slotKey,
              actualExecutionQty: String(quantities[s.slotKey] ?? 0),
            };
          })
          .filter(Boolean) as any[],
        remarks: remarks.trim() || undefined,
        ...(measuredPayload.length ? { measured: measuredPayload } : {}),
        measuredScope: (trackedFilterOn ? "MONITORED" : "ALL") as MeasuredScope,
      });
      if (res?.status !== true) {
        message.error(res?.message || "Could not save the execution entry");
        return;
      }
      message.success(res.data?.message || "Execution saved");
      if (res.data?.warning) message.warning(res.data.warning, 6);

      // Evidence photos ride to DMS after the save — a failed upload never
      // fails the day; the files stay attached in the drawer for a retry.
      const entryId = Number(res.data?.entryId);
      let photosFailed = false;
      if (entryId) {
        for (const [taskId, m] of Object.entries(measured)) {
          if (!m.photos?.length || m.value === "") continue;
          const line = preview.find((l) => l.taskId === Number(taskId));
          try {
            await uploadActualPhotosWeb({
              entryId,
              taskLabel: line?.boqNo || `Task ${taskId}`,
              executionDate: toDateOnly(executionDate.toDate()),
              files: m.photos,
            });
          } catch {
            photosFailed = true;
          }
        }
      }
      if (photosFailed) {
        message.warning("Saved — but some photos failed to upload. Re-open and save again to retry.", 6);
        return; // keep the drawer open so the photos are not lost
      }
      onSaved();
      onClose();
    } catch (err: any) {
      message.error(err?.message || "Could not save the execution entry");
    } finally {
      setSaving(false);
    }
  };

  const sectionLabel = (text: string, hint?: string) => (
    <div style={{ marginBottom: 10 }}>
      <div className="pms-eyebrow">{text}</div>
      {hint && <div style={{ fontSize: 12, color: "#8094ae", marginTop: 2 }}>{hint}</div>}
    </div>
  );

  // The day's stretches behind ONE line — the slots its contributions come
  // from. This is what the actual is recorded against, stretch-wise.
  const stretchesForLine = (line: any): LineStretch[] => {
    // The norm this line carries per unit of advance in each contributing
    // slot — what converts "metres advanced" on a stretch into the line's UOM.
    const normBySlot = new Map<string, number>(
      (line.contributions || []).map((c: any) => [c.slotKey, Number(c.norm)])
    );
    const list: LineStretch[] = [];
    for (const slot of activeSlots) {
      if (!normBySlot.has(slot.slotKey)) continue;
      for (const stretch of (stretches[slot.slotKey] || []).filter(isCompleteStretch)) {
        list.push({
          fromChainage: stretch.fromChainage,
          toChainage: stretch.toChainage,
          length: stretchLength(stretch),
          slotLabel: slot.slotLabel,
          norm: normBySlot.get(slot.slotKey) ?? 0,
        });
      }
    }
    return list;
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // The derived-lines table alone needs ~700px of columns, so 780 left it
      // scrolling sideways inside a drawer. Capped in vw as well as px: a fixed
      // 1180 would overflow a 1280 laptop and put the scrollbar back.
      width="min(1180px, 92vw)"
      destroyOnClose
      title={isEdit || existingEntryId ? "Edit Daily Execution" : "Add Daily Execution"}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Tooltip
            title={
              dayConflicts.size
                ? "Overlapping stretches claim the same ground"
                : hasQuantity
                  ? ""
                  : "Record what was executed at the face"
            }
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!hasQuantity || notConfigured || noVersionForDate || dayConflicts.size > 0}
              onClick={handleSave}
            >
              Save
            </Button>
          </Tooltip>
        </Space>
      }
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {/* ---------------------------------------------- where and when */}
      {sectionLabel("Where and when")}
      <div className="pms-exec-bar" style={{ marginBottom: 8, gap: 16 }}>
        <div>
          <div className="pms-exec-field-label">Date</div>
          <DatePicker
            value={executionDate}
            onChange={(v) => v && setExecutionDate(v)}
            allowClear={false}
            format="DD-MMM-YYYY"
            disabled={isEdit}
            style={{ width: 165 }}
          />
        </div>
        <div>
          <div className="pms-exec-field-label">Shift</div>
          <Select
            value={shiftId}
            onChange={handleShiftChange}
            style={{ width: 165 }}
            allowClear
            placeholder="Whole day"
            disabled={isEdit}
            options={shiftOptions.map((s: any) => ({ value: s.shiftId, label: s.shiftName }))}
          />
        </div>
        <div>
          <div className="pms-exec-field-label">
            Hours worked <span style={{ color: "#b8c2d0" }}>· optional</span>
          </div>
          <TimePicker.RangePicker
            value={workWindow as any}
            onChange={(v) => setWorkWindow(v as any)}
            format={TIME_FORMAT}
            use12Hours
            minuteStep={15}
            order={false}
            placeholder={["Start", "End"]}
            style={{ width: 250 }}
          />
        </div>
      </div>
      {windowHours && (
        <div style={{ fontSize: 12, color: "#8094ae", marginBottom: 12 }}>{windowHours}</div>
      )}

      <div className="pms-exec-bar" style={{ marginBottom: 6, gap: 16 }}>
        <div>
          <div className="pms-exec-field-label">Phase</div>
          <Select
            value={phaseId}
            onChange={(v) => {
              setPhaseId(v);
              setActivityId(undefined);
            }}
            style={{ width: 250 }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All phases"
            disabled={isEdit}
            options={phaseOptions}
          />
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div className="pms-exec-field-label">Activity / face</div>
          <Select
            value={activityId}
            onChange={setActivityId}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            placeholder="Select the face that was worked"
            disabled={isEdit}
            options={activityOptions}
          />
        </div>
      </div>
      {/* What this face has already taken — the chain, so the next stretch is
          typed against something rather than from memory. Falls back to the
          one-line chip when the ledger has a face but no reaches yet. */}
      {faceChain?.reaches?.length ? (
        <CapturedChain chain={faceChain} stretches={typedStretches} markClashes={!isEdit} />
      ) : (
        faceChain &&
        faceChain.faceChainage !== "" && (
          <Tag color="blue" style={{ marginTop: 8 }}>
            Face at CH {fmtQty(faceChain.faceChainage)}
            {faceChain.percentByChainage !== ""
              ? ` · ${faceChain.percentByChainage}% by chainage`
              : ` · ${fmtQty(faceChain.coveredLength)} m covered`}
          </Tag>
        )
      )}
      <div style={{ marginBottom: 22 }}>
        {isEdit && (
          <div style={{ fontSize: 12, color: "#8094ae", marginTop: 6 }}>
            <InfoCircleOutlined /> Date, shift and activity identify this entry — delete and
            re-add to move it.
          </div>
        )}
      </div>

      {isFuture && (
        <Alert
          type="warning"
          showIcon
          message="This date is in the future"
          style={{ marginBottom: 16 }}
        />
      )}
      {!isEdit && existingEntryId && (
        <Alert
          type="info"
          showIcon
          message="An entry already exists for this face, date and shift — you're editing it."
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Which matrix this day will be derived against. Shown only when it is
          NOT the current one — a banner on every routine entry would be noise,
          but a back-dated DPR silently using an older matrix would be a
          surprise. */}
      {!!dateVersion?.normsInForce &&
        !!dateVersion.effectiveFrom &&
        dayjs(dateVersion.effectiveFrom).isBefore(dayjs(), "day") &&
        executionDate.isBefore(dayjs(), "day") && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Using the execution norms effective ${dayjs(
              dateVersion.effectiveFrom
            ).format("DD-MMM-YYYY")} — the version in force on this date.`}
          />
        )}
      {!!dateVersion?.isStale && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This day's figures were derived under an older norm version"
          description="A revision has since been back-dated over this date. Saving now will re-derive it against the version currently in force."
        />
      )}

      {/* ------------------------------------------ what was executed */}
      {!activityId ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Pick an activity to record what was executed"
        />
      ) : noVersionForDate ? (
        <Empty
          description={
            <span>
              No execution norms were in force on{" "}
              <strong>{executionDate.format("DD-MMM-YYYY")}</strong> for{" "}
              <strong>
                {selectedActivity ? activityLabel(selectedActivity) : "this activity"}
              </strong>
              .
              <br />
              <span style={{ color: "#8094ae", fontSize: 12 }}>
                Pick a later date, or add a version covering this one on the
                Execution Norms screen.
              </span>
            </span>
          }
        >
          <Button type="primary" onClick={() => onConfigureNorms(activityId)}>
            Open Execution Norms
          </Button>
        </Empty>
      ) : notConfigured ? (
        <Empty
          description={
            <span>
              No execution norms configured for{" "}
              <strong>
                {selectedActivity ? activityLabel(selectedActivity) : "this activity"}
              </strong>
            </span>
          }
        >
          <Button type="primary" onClick={() => onConfigureNorms(activityId)}>
            Configure Execution Norms
          </Button>
        </Empty>
      ) : (
        <Spin spinning={loadingNorms || loadingEntry}>
          {sectionLabel(
            "Executed at the face",
            "Record each class's advance as stretches — everything below is derived from them."
          )}

          {/* One block per slot: rock-class slots take stretches ONLY (the
              quantity derives); the cum method/zone slots keep a plain input. */}
          <div style={{ maxWidth: 620, marginBottom: 8 }}>
            {activeSlots.map((slot) => {
              const rows = stretches[slot.slotKey] || [];
              const stretchable = supportsStretches(slot) || rows.length > 0;
              const derived = effectiveQuantities[slot.slotKey];
              const legacyQty = quantities[slot.slotKey];
              return (
                <div
                  key={slot.slotKey}
                  style={{ borderBottom: "1px solid #eef1f7", padding: "10px 0" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, color: "#022c57" }}>{slot.slotLabel}</span>{" "}
                      <span style={{ color: "#8094ae", fontSize: 12 }}>
                        {slot.uomName || (stretchable ? "m" : "")}
                      </span>
                    </div>
                    {stretchable ? (
                      <span className="tabular-nums" style={{ fontWeight: 600, color: "#022c57" }}>
                        {rows.length
                          ? `${fmtQty(derived ?? 0)} `
                          : legacyQty != null
                            ? `${fmtQty(legacyQty)} `
                            : "— "}
                        <span style={{ fontWeight: 400, color: "#8094ae", fontSize: 12 }}>
                          {rows.length
                            ? "from stretches"
                            : legacyQty != null
                              ? "recorded earlier"
                              : ""}
                        </span>
                      </span>
                    ) : (
                      <InputNumber
                        value={quantities[slot.slotKey] ?? null}
                        onChange={(v) => setQuantities((c) => ({ ...c, [slot.slotKey]: v }))}
                        style={{ width: 140 }}
                        placeholder="0.00"
                        step={0.01}
                      />
                    )}
                  </div>

                  {stretchable && (
                    <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                      {rows.map((stretch, idx) => {
                        const conflict = dayConflicts.get(`${slot.slotKey}:${idx}`);
                        return (
                          <div key={idx}>
                            <Space size={8}>
                              <InputNumber
                                value={
                                  stretch.fromChainage === "" ? null : Number(stretch.fromChainage)
                                }
                                onChange={(v) => {
                                  const next = rows.slice();
                                  next[idx] = {
                                    ...stretch,
                                    fromChainage: v == null ? "" : String(v),
                                  };
                                  patchStretches(slot.slotKey, next);
                                }}
                                placeholder="From CH"
                                step={0.01}
                                style={{ width: 130 }}
                                status={conflict ? "error" : undefined}
                              />
                              <span style={{ color: "#8094ae" }}>→</span>
                              <InputNumber
                                value={stretch.toChainage === "" ? null : Number(stretch.toChainage)}
                                onChange={(v) => {
                                  const next = rows.slice();
                                  next[idx] = {
                                    ...stretch,
                                    toChainage: v == null ? "" : String(v),
                                  };
                                  patchStretches(slot.slotKey, next);
                                }}
                                placeholder="To CH"
                                step={0.01}
                                style={{ width: 130 }}
                                status={conflict ? "error" : undefined}
                              />
                              <span className="tabular-nums" style={{ color: "#8094ae", width: 70, display: "inline-block", textAlign: "right" }}>
                                {isCompleteStretch(stretch) ? `${fmtQty(stretchLength(stretch))} m` : "—"}
                              </span>
                              <Button
                                type="text"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={() =>
                                  patchStretches(
                                    slot.slotKey,
                                    rows.filter((_, j) => j !== idx)
                                  )
                                }
                              />
                            </Space>
                            {conflict && (
                              <div style={{ color: "#e85347", fontSize: 12 }}>
                                overlaps {slotLabelOf(conflict.otherSlotKey)} · CH{" "}
                                {conflict.otherFrom} → {conflict.otherTo}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        style={{ padding: 0, width: "fit-content" }}
                        onClick={() => addStretch(slot.slotKey)}
                      >
                        Add stretch
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Geometry verdicts — the disabled Save explains itself. */}
          {dayConflicts.size > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12, maxWidth: 620 }}
              message={`${dayConflicts.size} stretch${dayConflicts.size === 1 ? "" : "es"} claim the same ground — one metre of tunnel is one rock class. Fix the flagged rows to save.`}
            />
          )}
          {incompleteCount > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12, maxWidth: 620 }}
              message={`${incompleteCount} incomplete stretch${incompleteCount === 1 ? "" : "es"} will be ignored — finish or remove ${incompleteCount === 1 ? "it" : "them"}.`}
            />
          )}

          {/* ---------------------------------------------- the result */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            {sectionLabel(
              "What that produced",
              !hasQuantity
                ? "Record a stretch above to see what it derives"
                : monitoring.configured
                  ? `${trackedPreviewCount} tracked of ${preview.length} BOQ line${preview.length === 1 ? "" : "s"} · ${
                      preview.filter((l) => measured[l.taskId]?.value).length
                    }/${preview.length} actuals recorded — expand a row for the arithmetic`
                  : `${preview.length} BOQ line${preview.length === 1 ? "" : "s"} · ${
                      preview.filter((l) => measured[l.taskId]?.value).length
                    }/${preview.length} actuals recorded — expand a row for the arithmetic`
            )}
            {monitoring.configured && hasQuantity && (
              <Space size={8} style={{ marginBottom: 10 }}>
                <Switch
                  size="small"
                  checked={trackedOnly}
                  onChange={setTrackedOnly}
                />
                <span style={{ fontSize: 12, color: "#364a63" }}>Tracked lines only</span>
                {trackedFilterOn && hiddenCount > 0 && (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontSize: 12 }}
                    onClick={() => setTrackedOnly(false)}
                  >
                    Show all ({hiddenCount} hidden)
                  </Button>
                )}
              </Space>
            )}
          </div>
          {trackedFilterOn && preview.length > 0 && visiblePreview.length === 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`None of the ${preview.length} derived line${preview.length === 1 ? " is" : "s are"} tracked on this date`}
              action={
                <Button size="small" onClick={() => setTrackedOnly(false)}>
                  Show all
                </Button>
              }
            />
          )}
          {visiblePreview.length ? (
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              scroll={{ y: 280 }}
              dataSource={visiblePreview.map((line) => ({
                ...line,
                key: `t-${line.taskId}`,
                children: line.contributions.map((c) => ({
                  key: `t-${line.taskId}-${c.slotKey}`,
                  isContribution: true,
                  boqNo: c.slotLabel,
                  qty: c.qty,
                  norm: c.norm,
                  value: c.value,
                })),
              }))}
              columns={[
                {
                  title: "BOQ",
                  dataIndex: "boqNo",
                  width: 120,
                  render: (value: string, row: any) =>
                    row.isContribution ? (
                      <span style={{ color: "#8094ae" }}>{value}</span>
                    ) : (
                      <span style={{ fontWeight: 600, color: "#022c57" }}>
                        {value || "—"}
                        {monitoring.configured && !trackedFilterOn && isTrackedLine(Number(row.taskId)) && (
                          <Tooltip title="Tracked on this date">
                            <span className="pms-exec-count-badge">tracked</span>
                          </Tooltip>
                        )}
                      </span>
                    ),
                },
                {
                  title: "Description",
                  dataIndex: "taskName",
                  width: 170,
                  ellipsis: { showTitle: false },
                  render: (value: string, row: any) =>
                    row.isContribution ? (
                      ""
                    ) : (
                      <Tooltip title={value && value.length > 24 ? value : ""}>
                        <span>{value}</span>
                      </Tooltip>
                    ),
                },
                {
                  title: executedQtyTitle,
                  dataIndex: "qty",
                  width: 90,
                  align: "right" as const,
                  render: (v: number, row: any) =>
                    row.isContribution ? (
                      <span className="tabular-nums" style={{ color: "#8094ae" }}>{fmtQty(v)}</span>
                    ) : null,
                },
                {
                  title: "Norm",
                  dataIndex: "norm",
                  width: 100,
                  align: "right" as const,
                  render: (v: number, row: any) =>
                    row.isContribution ? (
                      <span className="tabular-nums" style={{ color: "#8094ae" }}>{fmtNorm(v)}</span>
                    ) : null,
                },
                {
                  title: "Till date",
                  width: 100,
                  align: "right" as const,
                  render: (_: any, row: any) => {
                    if (row.isContribution) return null;
                    const saved = savedLines[Number(row.taskId)];
                    if (!saved?.tillDatePercent) {
                      return <span style={{ color: "#8094ae" }}>—</span>;
                    }
                    return (
                      <Tooltip
                        title={`${saved.tillDateQty} of ${saved.workQuantity} ${saved.workUomName || ""} executed to this date`}
                      >
                        <span className="tabular-nums" style={{ color: "#364a63" }}>
                          {Number(saved.tillDatePercent).toFixed(2)}%
                        </span>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: "Target",
                  width: 130,
                  align: "right" as const,
                  render: (_: any, row: any) =>
                    row.isContribution ? (
                      <span className="tabular-nums" style={{ color: "#8094ae" }}>
                        {fmtQty(row.value)}
                      </span>
                    ) : (
                      <span className="tabular-nums" style={{ fontWeight: 700, color: "#022c57" }}>
                        {fmtQty(row.actualValue)}{" "}
                        <span style={{ fontWeight: 400, color: "#8094ae" }}>{row.workUomName}</span>
                      </span>
                    ),
                },
                {
                  // The measurement tier: pending until site records the actual
                  // work done — theory stands in for reporting, marked as such.
                  title: "Actual",
                  width: 150,
                  align: "right" as const,
                  render: (_: any, row: any) => {
                    if (row.isContribution) return null;
                    const entry = measured[Number(row.taskId)];
                    if (entry?.value) {
                      const variance = row.actualValue
                        ? ((Number(entry.value) - row.actualValue) / Math.abs(row.actualValue)) * 100
                        : null;
                      return (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={() => setActualTarget(row)}
                        >
                          <span className="tabular-nums" style={{ color: "#1ee0ac", fontWeight: 600 }}>
                            {fmtQty(entry.value)}
                            {variance != null
                              ? ` · ${variance > 0 ? "+" : ""}${variance.toFixed(1)}%`
                              : ""}
                          </span>
                        </Button>
                      );
                    }
                    return (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => setActualTarget(row)}
                      >
                        <Tag color="warning" style={{ marginRight: 0 }}>
                          pending
                        </Tag>
                      </Button>
                    );
                  },
                },
              ]}
            />
          ) : preview.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
          ) : null}

          <div style={{ marginTop: 20 }}>
            <div className="pms-exec-field-label">
              Remarks <span style={{ color: "#b8c2d0" }}>· optional</span>
            </div>
            <Input.TextArea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Why the day ran short or over — face flooded, blast delayed…"
            />
          </div>
        </Spin>
      )}

      {actualTarget && (
        <RecordActualModal
          line={actualTarget}
          stretches={stretchesForLine(actualTarget)}
          existing={measured[Number(actualTarget.taskId)] || null}
          onSave={(next) => {
            setMeasured((current) => ({ ...current, [Number(actualTarget.taskId)]: next }));
            setActualTarget(null);
          }}
          onClear={() => {
            setMeasured((current) => {
              const next = { ...current };
              delete next[Number(actualTarget.taskId)];
              return next;
            });
            setActualTarget(null);
          }}
          onCancel={() => setActualTarget(null)}
        />
      )}
    </Drawer>
  );
};

// Records the ACTUAL work done against one derived BOQ line — stretch-wise on
// stretch days (one number per stretch, summed), whole-line otherwise. Up to 3
// evidence photos attach here and upload after the day saves.
interface LineStretch {
  fromChainage: string;
  toChainage: string;
  length: number;
  slotLabel: string;
  // This line's norm per unit of advance in the stretch's class; 0 = none.
  norm: number;
}

type ActualEntryUnit = "advance" | "uom";

const RecordActualModal: React.FC<{
  line: any;
  stretches: LineStretch[];
  existing: MeasuredEntry | null;
  onSave: (entry: MeasuredEntry) => void;
  onClear: () => void;
  onCancel: () => void;
}> = ({ line, stretches, existing, onSave, onClear, onCancel }) => {
  const stretchMode = stretches.length > 0;
  const keyOf = (s: { fromChainage: string; toChainage: string }) =>
    `${Number(s.fromChainage)}→${Number(s.toChainage)}`;

  // `values` is the wire truth — the actual per stretch in the LINE'S unit.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    (existing?.stretchActuals || []).forEach((actual) => {
      initial[keyOf(actual)] = actual.value;
    });
    return initial;
  });
  // The site measures ADVANCE (metres along the stretch); the line's quantity
  // is that advance × the class's norm for this line — the same arithmetic
  // the target uses, applied to what was actually driven. So the default entry
  // unit is metres, converted at the norm, with the line's own unit one click
  // away for a directly measured volume/weight.
  const canEnterAdvance = stretchMode && stretches.every((stretch) => stretch.norm > 0);
  const [entryUnit, setEntryUnit] = useState<ActualEntryUnit>(canEnterAdvance ? "advance" : "uom");
  const [advance, setAdvance] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (!canEnterAdvance) return initial;
    stretches.forEach((stretch) => {
      const stored = (existing?.stretchActuals || []).find((actual) => keyOf(actual) === keyOf(stretch));
      if (stored && stored.value !== "" && Number.isFinite(Number(stored.value))) {
        initial[keyOf(stretch)] = String(Math.round((Number(stored.value) / stretch.norm) * 1000) / 1000);
      }
    });
    return initial;
  });
  const setAdvanceFor = (stretch: LineStretch, metres: string) => {
    const key = keyOf(stretch);
    setAdvance((current) => ({ ...current, [key]: metres }));
    setValues((current) => ({
      ...current,
      [key]:
        metres === "" || !Number.isFinite(Number(metres))
          ? ""
          : String(round4(Number(metres) * stretch.norm)),
    }));
  };
  const useStretchLengths = () =>
    stretches.forEach((stretch) => setAdvanceFor(stretch, String(stretch.length)));
  const switchUnit = (next: ActualEntryUnit) => {
    if (next === "advance") {
      // Re-express whatever is already typed in metres, so nothing is lost.
      const metres: Record<string, string> = {};
      stretches.forEach((stretch) => {
        const value = values[keyOf(stretch)];
        if (value !== undefined && value !== "" && Number.isFinite(Number(value))) {
          metres[keyOf(stretch)] = String(Math.round((Number(value) / stretch.norm) * 1000) / 1000);
        }
      });
      setAdvance(metres);
    }
    setEntryUnit(next);
  };
  const [scalar, setScalar] = useState(existing?.value ?? "");
  const [remarks, setRemarks] = useState(existing?.remarks ?? "");
  const [photos, setPhotos] = useState<File[]>(existing?.photos || []);

  const filled = stretches.filter(
    (stretch) =>
      values[keyOf(stretch)] !== undefined &&
      values[keyOf(stretch)] !== "" &&
      Number.isFinite(Number(values[keyOf(stretch)]))
  );
  const total = stretchMode
    ? round4(filled.reduce((sum, stretch) => sum + Number(values[keyOf(stretch)]), 0))
    : Number(scalar);
  const allEntered = stretchMode
    ? filled.length === stretches.length
    : scalar !== "" && Number.isFinite(Number(scalar));
  const variance =
    allEntered && line.actualValue
      ? ((total - line.actualValue) / Math.abs(line.actualValue)) * 100
      : null;
  const needsRemark = variance != null && Math.abs(variance) > DEVIATION_REMARK_PERCENT;
  const canSave = allEntered && (!needsRemark || remarks.trim() !== "");

  const buildEntry = (): MeasuredEntry => ({
    value: String(total),
    remarks,
    ...(stretchMode
      ? {
          stretchActuals: stretches.map((stretch) => ({
            fromChainage: stretch.fromChainage,
            toChainage: stretch.toChainage,
            value: values[keyOf(stretch)] ?? "",
          })),
        }
      : {}),
    ...(photos.length ? { photos } : {}),
  });

  return (
    <Modal
      open
      title="Record actual work done"
      onCancel={onCancel}
      footer={[
        existing ? (
          <Button key="clear" onClick={onClear}>
            Clear — back to pending
          </Button>
        ) : null,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" disabled={!canSave} onClick={() => onSave(buildEntry())}>
          Save actual
        </Button>,
      ]}
    >
      <div style={{ color: "#8094ae", marginBottom: 12 }}>
        {line.boqNo} — {line.taskName}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <span>Target (from norms)</span>
        <span className="tabular-nums" style={{ fontWeight: 600 }}>
          {fmtQty(line.actualValue)} {line.workUomName}
        </span>
      </div>

      {stretchMode ? (
        <div style={{ display: "grid", gap: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div className="pms-exec-field-label" style={{ marginBottom: 0 }}>
              Actual per stretch
              {entryUnit === "advance" ? (
                <span style={{ color: "#8094ae", fontWeight: 400 }}>
                  {" "}· metres advanced, converted at this class's norm
                </span>
              ) : (
                <span style={{ color: "#8094ae", fontWeight: 400 }}> · in {line.workUomName}</span>
              )}
            </div>
            {canEnterAdvance && (
              <Space size={8}>
                <Button size="small" type="link" onClick={useStretchLengths} style={{ padding: 0 }}>
                  Use stretch lengths
                </Button>
                <Segmented
                  size="small"
                  value={entryUnit}
                  onChange={(v) => switchUnit(v as ActualEntryUnit)}
                  options={[
                    { label: "m advanced", value: "advance" },
                    { label: line.workUomName || "unit", value: "uom" },
                  ]}
                />
              </Space>
            )}
          </div>
          {stretches.map((stretch) => {
            const key = keyOf(stretch);
            const converted = values[key];
            return (
              <div
                key={key}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
              >
                <span style={{ minWidth: 0 }}>
                  CH {stretch.fromChainage} → {stretch.toChainage}{" "}
                  <span style={{ color: "#8094ae", fontSize: 12 }}>
                    {fmtQty(stretch.length)} m · {stretch.slotLabel}
                  </span>
                  {entryUnit === "advance" && stretch.norm > 0 && (
                    <div style={{ color: "#8094ae", fontSize: 12 }}>
                      × {fmtNorm(stretch.norm)} {line.workUomName} per m
                    </div>
                  )}
                </span>
                {entryUnit === "advance" ? (
                  <Space size={8}>
                    <span className="tabular-nums" style={{ color: "#526484", fontSize: 12, minWidth: 150, textAlign: "right", whiteSpace: "nowrap" }}>
                      {converted !== undefined && converted !== ""
                        ? `= ${fmtQty(Number(converted))} ${line.workUomName}`
                        : ""}
                    </span>
                    <InputNumber
                      value={advance[key] === undefined || advance[key] === "" ? null : Number(advance[key])}
                      onChange={(v) => setAdvanceFor(stretch, v == null ? "" : String(v))}
                      placeholder={fmtQty(stretch.length)}
                      addonAfter="m"
                      step={0.01}
                      min={0}
                      style={{ width: 150 }}
                    />
                  </Space>
                ) : (
                  <InputNumber
                    value={converted === undefined || converted === "" ? null : Number(converted)}
                    onChange={(v) =>
                      setValues((current) => ({ ...current, [key]: v == null ? "" : String(v) }))
                    }
                    placeholder="0.00"
                    step={0.01}
                    style={{ width: 130 }}
                  />
                )}
              </div>
            );
          })}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eef1f7", paddingTop: 8 }}>
            <span style={{ fontWeight: 600 }}>Actual total</span>
            <span className="tabular-nums" style={{ fontWeight: 600 }}>
              {allEntered ? `${fmtQty(total)} ${line.workUomName}` : `${filled.length}/${stretches.length} entered`}
            </span>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 8 }}>
          <div className="pms-exec-field-label">Actual work done ({line.workUomName})</div>
          <InputNumber
            value={scalar === "" ? null : Number(scalar)}
            onChange={(v) => setScalar(v == null ? "" : String(v))}
            placeholder="0.00"
            step={0.01}
            style={{ width: 180 }}
          />
        </div>
      )}

      {variance != null && (
        <div style={{ color: needsRemark ? "#f4bd0e" : "#1ee0ac", fontSize: 12, marginBottom: 8 }}>
          {variance > 0 ? "+" : ""}
          {variance.toFixed(1)}% vs norm
          {needsRemark ? " — a remark is required past ±10%" : ""}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div className="pms-exec-field-label">
          Site photos ({photos.length}/{MAX_PHOTOS}){" "}
          <span style={{ color: "#b8c2d0" }}>· optional, uploaded when the day is saved</span>
        </div>
        <Upload
          listType="picture-card"
          accept="image/*"
          fileList={photos.map(
            (file, index) =>
              ({
                uid: `${index}`,
                name: file.name,
                status: "done",
                originFileObj: file,
                url: URL.createObjectURL(file),
              }) as any
          )}
          beforeUpload={(file) => {
            setPhotos((current) => [...current, file].slice(0, MAX_PHOTOS));
            return false; // keep local — uploads ride the day's save
          }}
          onRemove={(file) => {
            setPhotos((current) => current.filter((_, index) => `${index}` !== file.uid));
            return true;
          }}
          showUploadList={{ showPreviewIcon: false }}
        >
          {photos.length < MAX_PHOTOS && (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 4 }}>Add</div>
            </div>
          )}
        </Upload>
      </div>

      <div className="pms-exec-field-label">
        Remark{" "}
        <span style={{ color: "#b8c2d0" }}>
          {needsRemark ? "· required — why it differs from the norms" : "· optional"}
        </span>
      </div>
      <Input.TextArea
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        rows={2}
        maxLength={300}
        placeholder="Overbreak on crown…"
      />
    </Modal>
  );
};

export default DailyExecutionDrawer;
